import React, { useState, useRef, useEffect } from 'react';
import './Menu.css';
import { menuCategories, CONFIG } from '../data';

function Menu() {
  const [tab, setTab] = useState('rice');
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const cat = menuCategories.find(c => c.id === tab);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = (el.querySelector('.menu-card')?.offsetWidth || 260) + 16;
    el.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardWidth = (el.querySelector('.menu-card')?.offsetWidth || 260) + 16;
      const index = Math.round(el.scrollLeft / cardWidth);
      setActiveIndex(index);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [tab]);

  const handleTabChange = (id) => {
    setTab(id);
    setActiveIndex(0);
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  };

  return (
    <section id="menu" className="menu-section section">
      <div className="container">

        <span className="section-label">Our Specialties</span>
        <h2 className="section-heading">The <em>Grand Menu</em></h2>
        <p className="section-desc">
          Authentic recipes crafted with rare Chettinad spices and traditional
          techniques passed through generations.
        </p>

        {/* ── Tabs ── */}
        <div className="menu-tabs" role="tablist">
          {menuCategories.map(c => (
            <button
              key={c.id}
              role="tab"
              aria-selected={tab === c.id}
              className={`menu-tab${tab === c.id ? ' active' : ''}`}
              onClick={() => handleTabChange(c.id)}
            >
              <span className="menu-tab-icon">{c.icon}</span>
              {c.name}
            </button>
          ))}
        </div>

        {/* ── Carousel ── */}
        <div className="menu-carousel-wrapper">
          <button
            className="carousel-btn carousel-btn--prev"
            onClick={() => scroll(-1)}
            aria-label="Previous"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="menu-carousel" ref={scrollRef} role="list">
            {cat.items.map((item, i) => (
              <article key={i} className="menu-card" role="listitem">
                <div className="menu-card-inner">
                  <div className="menu-card-top">
                    <h3 className="menu-card-name">{item.name}</h3>
                    {item.badge && (
                      <span className="menu-badge">{item.badge}</span>
                    )}
                  </div>
                  <p className="menu-card-desc">{item.desc}</p>
                  {item.tags && (
                    <div className="menu-card-tags">
                      {item.tags.map((tag, j) => (
                        <span key={j} className="menu-card-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="menu-card-accent" />
              </article>
            ))}
          </div>

          <button
            className="carousel-btn carousel-btn--next"
            onClick={() => scroll(1)}
            aria-label="Next"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* ── Live dots ── */}
        <div className="menu-dots" role="presentation">
          {cat.items.map((_, i) => (
            <button
              key={i}
              className={`menu-dot${i === activeIndex ? ' active' : ''}`}
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                const cardWidth = (el.querySelector('.menu-card')?.offsetWidth || 260) + 16;
                el.scrollTo({ left: i * cardWidth, behavior: 'smooth' });
              }}
              aria-label={`Go to item ${i + 1}`}
            />
          ))}
        </div>

        {/* ── Footer CTA ── */}
        <div className="menu-footer">
          <p className="menu-footer-text">
            Explore our full menu of <strong>120+ authentic dishes</strong>
          </p>
          <a
            className="menu-wa-btn"
            href={`https://wa.me/${CONFIG.whatsappNumber}?text=Please send me the full menu`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              className="menu-wa-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Get Full Menu on WhatsApp
          </a>
        </div>

      </div>
    </section>
  );
}

export default Menu;